import React, { ReactElement, useState, useEffect } from "react";
import {
  AgGrid,
  Button,
  Flex,
  Text,
  LoadingIndicator,
  toast,
  RadioGroup,
  RadioButton,
} from "@jsq/ds/core";
import { useCreateTicketExportMutation } from "#graphql/supergraphHooks";
import { SPACING } from "#utils/spacing";
import { useTicketExportPolling } from "../../hooks/useTicketExportPolling";
import { getFilterVars, getSortVars } from "../../ticketsGraphqlDataSource";
import { exportPanel, exportPanelHeader, exportSection, divider } from "./styles_vanilla.css";
import type { ExportStatus } from "#graphql/supergraphTypes";
import type { TicketRowItem, FieldMetadataMap } from "../../types";

const STATUS_MESSAGES: Record<ExportStatus, string> = {
  PENDING: "Preparing export",
  READY: "Export ready",
  ERROR: "Export failed",
};

type ExportScope = "all" | "selected";

type FieldMetadataProvider = {
  getFieldMetadataMap: () => FieldMetadataMap;
};

export type ExportPanelProps = {
  api: AgGrid.GridApi;
  dataSource?: FieldMetadataProvider;
};

export const ExportPanel = ({ api, dataSource }: ExportPanelProps): ReactElement => {
  const [exportId, setExportId] = useState<string | null>(null);
  const [exportStatus, setExportStatus] = useState<ExportStatus | null>(null);
  const [exportScope, setExportScope] = useState<ExportScope>("all");
  const [selectedRows, setSelectedRows] = useState<TicketRowItem[]>([]);

  const [createTicketExport] = useCreateTicketExportMutation();

  const { isPollingInProgress } = useTicketExportPolling({
    exportId,
    onStatusChange: setExportStatus,
    enabled: !!exportId,
  });

  useEffect(() => {
    const onSelectionChanged = () => {
      const rows = api.getSelectedRows();
      setSelectedRows(rows);
      if (rows.length === 0 && exportScope === "selected") {
        setExportScope("all");
      }
    };

    api.addEventListener("selectionChanged", onSelectionChanged);

    return () => {
      api.removeEventListener("selectionChanged", onSelectionChanged);
    };
  }, [api, exportScope]);

  const handleExport = async () => {
    setExportStatus("PENDING");
    const fieldMetadataMap = dataSource?.getFieldMetadataMap();
    const filters = getFilterVars(
      api.getFilterModel(),
      fieldMetadataMap && fieldMetadataMap.size > 0 ? fieldMetadataMap : undefined,
    );
    const columnState = api.getColumnState().filter((col) => col.sort !== null);
    const sort = getSortVars(
      columnState.map((col) => ({ colId: col.colId, sort: col.sort as "asc" | "desc" })),
    );

    const ticketKeys = exportScope === "selected" ? selectedRows.map((row) => row.key) : null;

    try {
      const result = await createTicketExport({
        variables: { filters, sort: sort || undefined, ticketKeys },
      });

      const newExportId = result.data?.createTicketExport;
      if (!newExportId) throw new Error("Failed to create export job");
      setExportId(newExportId);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Export failed");
      setExportStatus(null);
      setExportId(null);
    }
  };

  return (
    <Flex flexDirection="column" className={exportPanel} data-testid="export-panel">
      <Flex alignItems="center" justifyContent="space-between" className={exportPanelHeader}>
        <Text variant="regular-md">Export to CSV</Text>
      </Flex>
      <div className={divider} />
      <Flex flexDirection="column" gap={SPACING.MEDIUM} className={exportSection}>
        <RadioGroup
          name="export-scope"
          onChange={(e) => setExportScope(e.target.value as ExportScope)}
        >
          <RadioButton
            value="all"
            checked={exportScope === "all"}
            name="export-scope"
            data-testid="export-all-radio"
          >
            <Text variant="secondary-md">All tickets</Text>
          </RadioButton>
          <RadioButton
            value="selected"
            checked={exportScope === "selected"}
            disabled={selectedRows.length === 0}
            name="export-scope"
            data-testid="export-selected-radio"
          >
            <Text variant="secondary-md">Selected tickets ({selectedRows.length})</Text>
          </RadioButton>
        </RadioGroup>
        <Button
          variant="primary"
          onClick={() => void handleExport()}
          icon="download"
          iconPosition="left"
          disabled={isPollingInProgress}
          isFullWidth
          data-testid="export-button"
        >
          Export
        </Button>
      </Flex>
      {isPollingInProgress && exportStatus && (
        <>
          <div className={divider} />
          <Flex
            flexDirection="column"
            className={exportSection}
            alignItems="center"
            gap={SPACING.SMALL}
          >
            <LoadingIndicator variant="primary" size="small" />
            <Text variant="secondary-sm" align="center" data-testid="export-status">
              {STATUS_MESSAGES[exportStatus]}
            </Text>
          </Flex>
        </>
      )}
    </Flex>
  );
};
